<template>
  <div class="dialogs-list">
    <article
      v-for="dialog in dialogs"
      :key="dialog.id"
      :class="[
        'dialogs-list-item',
        { 'is-active': isSelected(dialog) },
        { 'is-revealed': isRevealed(dialog) }
      ]"
      @click="handleSelect(dialog)"
      @contextmenu.prevent="handleContextMenu(dialog)"
      @touchstart.passive="handleTouchStart($event, dialog)"
      @touchend.passive="handleTouchEnd($event, dialog)"
    >
      <div class="dialogs-list-item__main">
        <el-avatar
          :size="54"
          :src="getAvatar(dialog)"
          class="dialogs-list-item__avatar"
        >
          <template v-if="!getAvatar(dialog)">{{ clientName(dialog.client_name) }}</template>
        </el-avatar>

        <div class="dialogs-list-item__body">
          <div class="dialogs-list-item__head">
            <div class="dialogs-list-item__title">
              <span class="dialogs-list-item__name">{{ dialog.client_name || 'Без имени' }}</span>
              <span v-if="dialog.messenger_type" class="dialogs-list-item__messenger">
                <img
                  v-if="getMessengerAsset(dialog.messenger_type)"
                  :src="getMessengerAsset(dialog.messenger_type)"
                  alt=""
                  class="dialogs-list-item__messenger-image"
                />
                <font-awesome-icon
                  v-else
                  :class="`dialogs-list-item__messenger--${dialog.messenger_type}`"
                  :icon="getMessengerIcon(dialog.messenger_type)"
                />
              </span>
            </div>
            <span class="dialogs-list-item__time">{{ formatLastMessageDate(dialog.last_active) }}</span>
          </div>

          <div class="dialogs-list-item__footer">
            <p class="dialogs-list-item__preview">{{ previewText(dialog) }}</p>
            <span v-if="Number(dialog.unread_count || 0) > 0" class="dialogs-list-item__badge">
              {{ dialog.unread_count }}
            </span>
          </div>
        </div>
      </div>

      <div v-if="isRevealed(dialog)" class="dialogs-list-item__actions">
        <button
          type="button"
          class="dialogs-list-action dialogs-list-action--archive"
          @click.stop="handleArchive(dialog)"
        >
          <img src="/img/dialogs/archive-icon.png" alt="" class="dialogs-list-action__icon" />
          <small>Архив</small>
        </button>
        <button
          type="button"
          class="dialogs-list-action dialogs-list-action--delete"
          @click.stop="handleDelete(dialog)"
        >
          <img src="/img/dialogs/delete-icon.png" alt="" class="dialogs-list-action__icon" />
          <small>Удалить</small>
        </button>
      </div>
    </article>

    <div v-if="!dialogs.length" class="dialogs-list-empty">Диалоги не найдены</div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'DialogsList',
  components: {
    FontAwesomeIcon
  },
  props: {
    dialogs: {
      type: Array,
      default: () => []
    },
    selectedDialogId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      revealedDialogId: null,
      touchStartX: null,
      touchDialogId: null
    }
  },
  mounted() {
    document.addEventListener('pointerdown', this.handleOutsidePointer)
  },
  beforeUnmount() {
    document.removeEventListener('pointerdown', this.handleOutsidePointer)
  },
  methods: {
    isSelected(dialog) {
      return String(this.selectedDialogId) === String(dialog.id)
    },
    isRevealed(dialog) {
      return String(this.revealedDialogId) === String(dialog.id)
    },
    handleSelect(dialog) {
      this.revealedDialogId = null
      this.$emit('select-dialog', dialog)
    },
    handleContextMenu(dialog) {
      if (!this.isSelected(dialog)) {
        this.$emit('select-dialog', dialog)
      }
      this.revealedDialogId = dialog.id
    },
    handleTouchStart(event, dialog) {
      this.touchDialogId = dialog.id
      this.touchStartX = event.changedTouches?.[0]?.clientX ?? null
    },
    handleTouchEnd(event, dialog) {
      if (String(this.touchDialogId) !== String(dialog.id) || this.touchStartX == null) {
        this.touchDialogId = null
        this.touchStartX = null
        return
      }

      const endX = event.changedTouches?.[0]?.clientX ?? this.touchStartX
      const deltaX = endX - this.touchStartX

      if (deltaX <= -42) {
        if (!this.isSelected(dialog)) {
          this.$emit('select-dialog', dialog)
        }
        this.revealedDialogId = dialog.id
      } else if (deltaX >= 32 && this.isRevealed(dialog)) {
        this.revealedDialogId = null
      }

      this.touchDialogId = null
      this.touchStartX = null
    },
    handleOutsidePointer(event) {
      if (!this.$el?.contains(event.target)) {
        this.revealedDialogId = null
      }
    },
    handleArchive(dialog) {
      this.revealedDialogId = null
      this.$emit('archive-dialog', dialog)
    },
    handleDelete(dialog) {
      this.revealedDialogId = null
      this.$emit('delete-dialog', dialog)
    },
    clientName(name) {
      if (!name) return ''
      const parts = name.trim().split(' ').filter(Boolean)
      if (parts.length === 0) return ''
      if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase()
      }
      return (parts[0][0] + parts[1][0]).toUpperCase()
    },
    getMessengerIcon(type) {
      switch (type) {
        case 'whatsapp':
          return ['fab', 'whatsapp']
        case 'telegram':
          return ['fab', 'telegram']
        case 'vk':
          return ['fab', 'vk']
        case 'email':
          return ['far', 'envelope']
        default:
          return ['far', 'circle-question']
      }
    },
    getMessengerAsset(type) {
      switch (type) {
        case 'whatsapp':
          return '/img/dialogs/messenger-whatsapp.png'
        case 'telegram':
          return '/img/dialogs/messenger-telegram.png'
        case 'vk':
          return '/img/dialogs/messenger-vk.png'
        case 'facebook':
          return '/img/dialogs/messenger-facebook.png'
        case 'email':
          return '/img/dialogs/messenger-email.png'
        default:
          return ''
      }
    },
    getAvatar(dialog) {
      if (dialog.client_avatar) return dialog.client_avatar
      if (dialog.client_sex === 1) return '/img/avatar-male.svg'
      if (dialog.client_sex === 2) return '/img/avatar-female.svg'
      return null
    },
    previewText(dialog) {
      return (
        dialog.last_message_preview ||
        dialog.last_message ||
        dialog.last_message_text ||
        dialog.client_phone ||
        'Диалог без сообщений'
      )
    },
    formatLastMessageDate(date) {
      if (!date) return ''
      const parsed = new Date(String(date).replace(' ', 'T'))
      if (Number.isNaN(parsed.getTime())) return ''

      const now = new Date()
      const today = parsed.toDateString() === now.toDateString()
      const yesterday = new Date(now)
      yesterday.setDate(now.getDate() - 1)
      const isYesterday = parsed.toDateString() === yesterday.toDateString()

      if (today) {
        return parsed.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
      }
      if (isYesterday) {
        return 'Вчера'
      }

      const diffDays = Math.floor((now - parsed) / 86400000)
      if (diffDays < 7) {
        const weekday = parsed.toLocaleDateString('ru-RU', { weekday: 'short' })
        return weekday.slice(0, 1).toUpperCase() + weekday.slice(1, 2)
      }

      return parsed.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
    }
  }
}
</script>

<style scoped>
.dialogs-list {
  height: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 0;
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 185, 255, 0.26) transparent;
}

.dialogs-list::-webkit-scrollbar {
  width: 10px;
}

.dialogs-list::-webkit-scrollbar-track {
  background: transparent;
}

.dialogs-list::-webkit-scrollbar-thumb {
  border: 3px solid transparent;
  border-radius: 999px;
  background: rgba(148, 185, 255, 0.22);
  background-clip: padding-box;
}

.dialogs-list-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  overflow: hidden;
  border-radius: 16px;
  background: linear-gradient(90deg, rgba(0, 22, 54, 0.72) 0%, rgba(0, 61, 152, 0.55) 100%);
  box-shadow: inset 0 0 0 1px rgba(0, 35, 89, 1), 0 3px 6px 1px rgba(0, 0, 0, 0.35);
  cursor: pointer;
  transition: background 0.18s ease, box-shadow 0.18s ease;
}

.dialogs-list-item:hover {
  box-shadow: inset 0 0 0 1px rgba(19, 79, 173, 0.95), 0 3px 6px 1px rgba(0, 0, 0, 0.42);
}

.dialogs-list-item.is-active {
  background: rgba(0, 22, 54, 0.92);
  box-shadow: inset 0 0 0 2px #22c0ff, 0 3px 6px 1px rgba(0, 0, 0, 0.42);
}

.dialogs-list-item.is-revealed {
  grid-template-columns: minmax(0, 1fr) 138px;
}

.dialogs-list-item__main {
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  min-height: 76px;
  min-width: 0;
  padding: 8px 14px 8px 12px;
}

.dialogs-list-item__avatar {
  width: 54px;
  height: 54px;
  flex-shrink: 0;
  background: #e8edf4;
  color: #45607f;
  font-family: var(--ui-font-family-base);
  font-size: 18px;
}

.dialogs-list-item__body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.dialogs-list-item__head,
.dialogs-list-item__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.dialogs-list-item__title {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.dialogs-list-item__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #ffffff;
  font-family: var(--ui-font-family-base);
  font-size: 16px;
  font-weight: 400;
  line-height: 1;
}

.dialogs-list-item__messenger {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.dialogs-list-item__messenger-image {
  display: block;
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.dialogs-list-item__messenger--telegram,
.dialogs-list-item__messenger--whatsapp,
.dialogs-list-item__messenger--vk,
.dialogs-list-item__messenger--email,
.dialogs-list-item__messenger--facebook {
  font-size: 14px;
}

.dialogs-list-item__time {
  flex-shrink: 0;
  color: #c9c9c9;
  font-family: var(--ui-font-family-base);
  font-size: 14px;
  font-weight: 300;
  line-height: 1;
}

.dialogs-list-item__preview {
  min-width: 0;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #22c0ff;
  font-family: var(--ui-font-family-base);
  font-size: 12px;
  font-weight: 300;
  line-height: 1.08;
}

.dialogs-list-item.is-active .dialogs-list-item__preview {
  color: #d8e6ff;
}

.dialogs-list-item__badge {
  flex-shrink: 0;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #22c0ff;
  color: #001636;
  font-family: var(--ui-font-family-base);
  font-size: 12px;
  font-weight: 400;
  line-height: 1;
}

.dialogs-list-item__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-width: 138px;
  overflow: hidden;
  border-radius: 0 16px 16px 0;
  box-shadow: inset 1px 0 0 rgba(0, 0, 0, 0.16);
}

.dialogs-list-action {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: none;
  padding: 0;
  font-family: var(--ui-font-family-base);
  text-align: center;
  cursor: pointer;
}

.dialogs-list-action__icon {
  display: block;
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.dialogs-list-action--archive {
  border-radius: 16px 0 0 16px;
  background: linear-gradient(90deg, #44b4e2 0%, #22c0ff 100%);
  color: #001636;
}

.dialogs-list-action--delete {
  border-radius: 0 16px 16px 0;
  background: #ff4646;
  color: #ffffff;
}

.dialogs-list-action small {
  font-size: 12px;
  font-weight: 300;
  line-height: 1;
}

.dialogs-list-action:focus-visible {
  outline: none;
  box-shadow: none;
}

.dialogs-list-empty {
  width: 100%;
  box-sizing: border-box;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: linear-gradient(180deg, #133264 0%, #0f2751 100%);
  box-shadow: inset 0 0 0 1px rgba(52, 96, 167, 0.5);
  color: #b9d5ff;
  font-family: var(--ui-font-family-base);
  font-size: 18px;
}

@media (max-width: 900px) {
  .dialogs-list-item__main {
    min-height: 80px;
  }
}

@media (max-width: 540px) {
  .dialogs-list-item.is-revealed {
    grid-template-columns: minmax(0, 1fr) 116px;
  }

  .dialogs-list-item__main {
    padding: 10px 12px;
  }

  .dialogs-list-item__name {
    font-size: 15px;
  }

  .dialogs-list-item__preview,
  .dialogs-list-item__time {
    font-size: 12px;
  }

  .dialogs-list-item__actions {
    min-width: 116px;
  }

  .dialogs-list-action__icon {
    width: 22px;
    height: 22px;
  }

  .dialogs-list-action small {
    font-size: 11px;
  }
}
</style>
